import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  createShipment,
  updateShipment,
  deleteShipment,
  fetchShipmentDetail,
  fetchDropdowns,
  clearDetail,
  clearPendingItems,
  removePendingItem,
  deleteShipmentItem,
  patchDraftShipment,
  resetDraftShipment,
  emptyDraftShipment,
  createShipmentLabel,
  createCustomTransportProfile,
  fetchShipmentLabels,
  changeShipmentStatus,
} from '../../store/slices/shipmentTrackerSlice';
import { BOARD_STATUSES } from '../../components/shipment-tracker/ShipmentBoard';
import { ShipmentStatusRequest } from '../../api/generated/models/ShipmentStatusRequest';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { addNotification } from '../../store/slices/notificationSlice';
import { useShipmentTrackerPermissions } from '../../hooks/useShipmentTrackerPermissions';
import ArrowBack from '../../components/ArrowBack';
import ConfirmDeleteShipmentModal from '../../components/shipment-tracker/ConfirmDeleteShipmentModal';
import ShipmentLabelsField from '../../components/shipment-tracker/ShipmentLabelsField';
import LabelModal from '../../components/shipment-tracker/LabelModal';
import TransportProfileModal from '../../components/shipment-tracker/TransportProfileModal';
import { ShipmentCreateRequest } from '../../api/generated/models/ShipmentCreateRequest';
import { ShipmentLabelRequest } from '../../api/generated/models/ShipmentLabelRequest';
import { ApiError } from '../../api/generated/core/ApiError';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';

interface ShipmentTrackerCreatePageProps {
  isEditing?: boolean;
}

type WorkflowStatus = ShipmentCreateRequest.workflowStatus;
type Currency = ShipmentCreateRequest.currency;
type ExecutionStatus = ShipmentStatusRequest.executionStatus;

const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', USD: '$' };

const INCOTERM_DESCRIPTIONS: Record<string, string> = {
  EXW: 'Ex Works - seller makes goods available at premises. Buyer bears all costs and risks.',
  FCA: 'Free Carrier - seller delivers goods to named carrier. Risk transfers at delivery.',
  CPT: 'Carriage Paid To - seller pays freight to destination. Risk transfers at first carrier.',
  CIP: 'Carriage and Insurance Paid To - like CPT but seller also provides insurance.',
  DAP: 'Delivered at Place - seller delivers to named destination. Buyer handles import duties.',
  DPU: 'Delivered at Place Unloaded - seller delivers and unloads at destination.',
  DDP: 'Delivered Duty Paid - seller bears all costs and risks including import duties.',
  FAS: 'Free Alongside Ship - seller delivers goods alongside vessel at named port.',
  FOB: 'Free On Board - seller loads goods on board vessel at named port.',
  CFR: 'Cost and Freight - seller pays cost and freight to destination port.',
  CIF: 'Cost, Insurance and Freight - like CFR but seller also provides insurance.',
};

const ShipmentTrackerCreatePage: React.FC<ShipmentTrackerCreatePageProps> = ({
  isEditing = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { shipmentId } = useParams<{ shipmentId?: string }>();
  const { canWrite, canViewFinancials } = useShipmentTrackerPermissions();

  const { shipment: existingShipment } = useAppSelector(
    (state) => state.shipmentTracker.detail,
  );
  const {
    transportProfiles,
    incoterms,
    countries,
    loaded: dropdownsLoaded,
  } = useAppSelector((state) => state.shipmentTracker.dropdowns);
  const pendingItems = useAppSelector(
    (state) => state.shipmentTracker.pendingItems,
  );
  // Draft persisted in Redux so header fields survive navigating to the item
  // form and back. Only used for a new shipment; editing hydrates from the server.
  const draft = useAppSelector((state) => state.shipmentTracker.draftShipment);
  const labelCatalog = useAppSelector((state) => state.shipmentTracker.labels.items);
  const settings = useAppSelector((state) => state.shipmentTracker.settings.data);

  // Form state (initialised from the Redux draft on a new shipment)
  const [name, setName] = useState(() => (isEditing ? '' : draft.name));
  const [currency, setCurrency] = useState<Currency>(() =>
    isEditing
      ? ShipmentCreateRequest.currency.EUR
      : (draft.currency as Currency),
  );
  const [transportProfileId, setTransportProfileId] = useState(() =>
    isEditing ? '' : draft.transportProfileId,
  );
  const [incoterm, setIncoterm] = useState(() =>
    isEditing ? '' : draft.incoterm,
  );
  const [originCountryCode, setOriginCountryCode] = useState(() =>
    isEditing ? '' : draft.originCountryCode,
  );
  const [destinationCountryCode, setDestinationCountryCode] = useState(() =>
    isEditing ? '' : draft.destinationCountryCode,
  );
  const [originCity, setOriginCity] = useState(() =>
    isEditing ? '' : draft.originCity,
  );
  const [destinationCity, setDestinationCity] = useState(() =>
    isEditing ? '' : draft.destinationCity,
  );
  const [eta, setEta] = useState(() => (isEditing ? '' : draft.eta));
  const [warehouseComment, setWarehouseComment] = useState(() =>
    isEditing ? '' : draft.warehouseComment,
  );

  // Planned costs
  const [plannedTransport, setPlannedTransport] = useState(() =>
    isEditing ? '' : draft.plannedTransport,
  );
  const [plannedDuty, setPlannedDuty] = useState(() =>
    isEditing ? '' : draft.plannedDuty,
  );
  const [plannedClearance, setPlannedClearance] = useState(() =>
    isEditing ? '' : draft.plannedClearance,
  );
  const [plannedEnergyTax, setPlannedEnergyTax] = useState(() =>
    isEditing ? '' : draft.plannedEnergyTax,
  );
  const [labelIds, setLabelIds] = useState<string[]>(() =>
    isEditing ? [] : draft.labelIds,
  );
  const [executionStatus, setExecutionStatus] = useState<ExecutionStatus>(
    ShipmentStatusRequest.executionStatus.PROGRESS,
  );
  const [trackingUrl, setTrackingUrl] = useState(() =>
    isEditing ? '' : draft.trackingUrl,
  );

  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelModalError, setLabelModalError] = useState<string | null>(null);
  const [labelModalSaving, setLabelModalSaving] = useState(false);
  const [showTransportModal, setShowTransportModal] = useState(false);
  const [transportModalError, setTransportModalError] = useState<string | null>(null);
  const [transportModalSaving, setTransportModalSaving] = useState(false);

  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Track whether shipment was ever saved (workflowStatus === 'saved')
  const isSaved = existingShipment?.workflowStatus === 'saved';

  useEffect(() => {
    if (!dropdownsLoaded) {
      dispatch(fetchDropdowns());
    }
  }, [dispatch, dropdownsLoaded]);

  useEffect(() => {
    if (isEditing && shipmentId) {
      dispatch(fetchShipmentDetail(shipmentId));
    }
    return () => {
      if (!isEditing) {
        // Staged items survive this: opening the item form unmounts the page, and the draft
        // is only discarded on save or when starting a new shipment.
        dispatch(clearDetail());
      }
    };
  }, [dispatch, isEditing, shipmentId]);

  // Populate form when editing
  useEffect(() => {
    if (isEditing && existingShipment) {
      setName(existingShipment.name);
      setCurrency(
        (existingShipment.currency as Currency) ??
        ShipmentCreateRequest.currency.EUR,
      );
      setTransportProfileId(existingShipment.transportProfileId ?? '');
      setIncoterm(existingShipment.incoterm ?? '');
      setOriginCountryCode(existingShipment.originCountryCode ?? '');
      setDestinationCountryCode(existingShipment.destinationCountryCode ?? '');
      setOriginCity(existingShipment.originCity ?? '');
      setDestinationCity(existingShipment.destinationCity ?? '');
      setEta(existingShipment.eta ?? '');
      setWarehouseComment(existingShipment.warehouseComment ?? '');
      setPlannedTransport(
        existingShipment.plannedTransport != null
          ? String(existingShipment.plannedTransport)
          : '',
      );
      setPlannedDuty(
        existingShipment.plannedDuty != null
          ? String(existingShipment.plannedDuty)
          : '',
      );
      setPlannedClearance(
        existingShipment.plannedClearance != null
          ? String(existingShipment.plannedClearance)
          : '',
      );
      setPlannedEnergyTax(
        existingShipment.plannedEnergyTax != null
          ? String(existingShipment.plannedEnergyTax)
          : '',
      );
      setLabelIds((existingShipment.labels ?? []).map((l) => l.id));
      setTrackingUrl(existingShipment.trackingUrl ?? '');
      const status = existingShipment.executionStatus as ExecutionStatus | null;
      setExecutionStatus(
        status && Object.values(ShipmentStatusRequest.executionStatus).includes(status)
          ? status
          : ShipmentStatusRequest.executionStatus.PROGRESS,
      );
    }
  }, [isEditing, existingShipment]);

  useEffect(() => {
    if (isEditing || !settings?.defaultCurrency) return;
    const draftIsPristine =
      draft.currency === emptyDraftShipment.currency &&
      draft.name === emptyDraftShipment.name &&
      draft.transportProfileId === emptyDraftShipment.transportProfileId;
    if (draftIsPristine && pendingItems.length === 0) {
      setCurrency(settings.defaultCurrency as Currency);
    }
  }, [isEditing, settings, draft, pendingItems.length]);

  // Nothing is persisted until "Save as draft" / "Save shipment", so a reload or tab close
  // discards the whole draft. In-app navigation is safe (Redux keeps it), hence unload only.
  const hasUnsavedDraft =
    !isEditing &&
    (pendingItems.length > 0 ||
      (
        Object.keys(emptyDraftShipment) as Array<
          keyof typeof emptyDraftShipment
        >
      ).some((field) => draft[field] !== emptyDraftShipment[field]));

  useUnsavedChangesWarning(hasUnsavedDraft);

  // Mirror the form into the Redux draft so it survives navigating to the item
  // form and back (new shipment only; the draft is reset when starting a new one).
  useEffect(() => {
    if (isEditing) return;
    dispatch(
      patchDraftShipment({
        name,
        currency,
        transportProfileId,
        incoterm,
        originCountryCode,
        destinationCountryCode,
        originCity,
        destinationCity,
        eta,
        warehouseComment,
        plannedTransport,
        plannedDuty,
        plannedClearance,
        plannedEnergyTax,
        labelIds,
        trackingUrl,
      }),
    );
  }, [
    isEditing,
    dispatch,
    name,
    currency,
    transportProfileId,
    incoterm,
    originCountryCode,
    destinationCountryCode,
    originCity,
    destinationCity,
    eta,
    warehouseComment,
    plannedTransport,
    plannedDuty,
    plannedClearance,
    plannedEnergyTax,
    labelIds,
    trackingUrl,
  ]);

  const buildRequest = useCallback(
    (workflowStatus: WorkflowStatus): ShipmentCreateRequest => ({
      name: name.trim(),
      workflowStatus,
      currency,
      transportProfileId: transportProfileId || null,
      incoterm: incoterm || null,
      originCountryCode: originCountryCode || null,
      destinationCountryCode: destinationCountryCode || null,
      originCity: originCity || null,
      destinationCity: destinationCity || null,
      eta: eta || null,
      warehouseComment: warehouseComment || null,
      plannedTransport: plannedTransport ? parseFloat(plannedTransport) : null,
      plannedDuty: plannedDuty ? parseFloat(plannedDuty) : null,
      plannedClearance: plannedClearance ? parseFloat(plannedClearance) : null,
      plannedEnergyTax: plannedEnergyTax ? parseFloat(plannedEnergyTax) : null,
      labelIds,
      trackingUrl: trackingUrl.trim() === '' ? null : trackingUrl.trim(),
      // Pending items included on first save (POST); on subsequent PUT they go via sub-routes
      items: pendingItems.length > 0 && !isEditing ? pendingItems : undefined,
    }),
    [
      name,
      currency,
      transportProfileId,
      incoterm,
      originCountryCode,
      destinationCountryCode,
      originCity,
      destinationCity,
      eta,
      warehouseComment,
      plannedTransport,
      plannedDuty,
      plannedClearance,
      plannedEnergyTax,
      labelIds,
      trackingUrl,
      pendingItems,
      isEditing,
    ],
  );

  const handleCreateLabel = async (payload: {
    name: string;
    color: ShipmentLabelRequest.color;
  }) => {
    setLabelModalSaving(true);
    setLabelModalError(null);
    try {
      const created = await dispatch(
        createShipmentLabel({ name: payload.name, color: payload.color }),
      ).unwrap();
      setLabelIds((ids) => [...ids, created.id]);
      dispatch(fetchShipmentLabels());
      setShowLabelModal(false);
    } catch (e) {
      setLabelModalError(
        e instanceof ApiError ? e.body?.error ?? t('error') : t('error'),
      );
    } finally {
      setLabelModalSaving(false);
    }
  };

  const handleCreateTransport = async (payload: {
    name: string;
    details: string | null;
  }) => {
    setTransportModalSaving(true);
    setTransportModalError(null);
    try {
      const created = await dispatch(
        createCustomTransportProfile({
          name: payload.name,
          details: payload.details,
        }),
      ).unwrap();
      setTransportProfileId(created.id);
      setShowTransportModal(false);
    } catch (e) {
      setTransportModalError(
        e instanceof ApiError ? e.body?.error ?? t('error') : t('error'),
      );
    } finally {
      setTransportModalSaving(false);
    }
  };

  const standardProfiles = transportProfiles.filter((p) => !p.isCustom);
  const customProfiles = transportProfiles.filter((p) => p.isCustom);

  /**
   * Adding the first item is the point where a shipment stops being a throwaway form, so it is
   * persisted as a draft right then. Nothing is created before that, so abandoning an empty form
   * still leaves no row behind. Needs a name — the API requires one — so if the item comes first
   * this fires as soon as the name is filled in.
   */
  const autoDraftRef = useRef(false);

  useEffect(() => {
    if (isEditing || autoDraftRef.current) return;
    if (pendingItems.length === 0 || !name.trim()) return;

    autoDraftRef.current = true;
    (async () => {
      try {
        const result = await dispatch(
          createShipment(
            buildRequest(ShipmentCreateRequest.workflowStatus.DRAFT),
          ),
        ).unwrap();
        dispatch(clearPendingItems());
        dispatch(resetDraftShipment());
        dispatch(
          addNotification({
            type: 'success',
            title: t('success'),
            message: t('shipment_auto_draft_created'),
          }),
        );
        navigate(`/features/shipment-tracker/shipments/${result.id}/edit`, {
          replace: true,
        });
      } catch {
        // Error notification fired in the thunk; allow a retry on the next change.
        autoDraftRef.current = false;
      }
    })();
  }, [isEditing, pendingItems, name, buildRequest, dispatch, navigate, t]);

  const validateForSave = () => {
    if (!name.trim()) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_error_name_required'),
        }),
      );
      return false;
    }
    if (!transportProfileId) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_error_transport_required'),
        }),
      );
      return false;
    }
    if (!incoterm) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_error_incoterm_required'),
        }),
      );
      return false;
    }
    if (!originCountryCode) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_error_origin_required'),
        }),
      );
      return false;
    }
    if (!destinationCountryCode) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_error_destination_required'),
        }),
      );
      return false;
    }
    return true;
  };

  const persistExecutionStatus = async (
    shipmentIdToUpdate: string,
    workflowStatus: WorkflowStatus,
  ) => {
    if (workflowStatus !== ShipmentCreateRequest.workflowStatus.SAVED) {
      return;
    }
    await dispatch(
      changeShipmentStatus({ id: shipmentIdToUpdate, executionStatus }),
    ).unwrap();
  };

  const handleSaveDraft = async () => {
    if (!name.trim()) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_error_name_required'),
        }),
      );
      return;
    }
    setSaving(true);
    try {
      const req = buildRequest(ShipmentCreateRequest.workflowStatus.DRAFT);
      if (isEditing && shipmentId) {
        await dispatch(updateShipment({ id: shipmentId, data: req })).unwrap();
      } else {
        const result = await dispatch(createShipment(req)).unwrap();
        dispatch(clearPendingItems());
        dispatch(resetDraftShipment());
        navigate(`/features/shipment-tracker/shipments/${result.id}/edit`, {
          replace: true,
        });
      }
      dispatch(
        addNotification({
          type: 'success',
          title: t('success'),
          message: t('shipment_saved_as_draft'),
        }),
      );
    } catch {
      // error notification fired in thunk
    } finally {
      setSaving(false);
    }
  };

  const handleSaveShipment = async () => {
    if (!validateForSave()) return;
    setSaving(true);
    try {
      const req = buildRequest(ShipmentCreateRequest.workflowStatus.SAVED);
      if (isEditing && shipmentId) {
        await dispatch(updateShipment({ id: shipmentId, data: req })).unwrap();
        await persistExecutionStatus(
          shipmentId,
          ShipmentCreateRequest.workflowStatus.SAVED,
        );
        dispatch(
          addNotification({
            type: 'success',
            title: t('success'),
            message: t('shipment_saved'),
          }),
        );
        navigate(`/features/shipment-tracker/shipments/${shipmentId}`);
      } else {
        const result = await dispatch(createShipment(req)).unwrap();
        await persistExecutionStatus(
          result.id,
          ShipmentCreateRequest.workflowStatus.SAVED,
        );
        dispatch(clearPendingItems());
        dispatch(resetDraftShipment());
        dispatch(
          addNotification({
            type: 'success',
            title: t('success'),
            message: t('shipment_created'),
          }),
        );
        navigate(`/features/shipment-tracker/shipments/${result.id}`);
      }
    } catch {
      // error notification fired in thunk
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const id = shipmentId ?? existingShipment?.id;
    if (!id) return;
    setSaving(true);
    try {
      await dispatch(deleteShipment(id)).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          title: t('success'),
          message: t('shipment_deleted'),
        }),
      );
      navigate('/features/shipment-tracker/shipments', { replace: true });
    } catch {
      // error notification fired in thunk
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
    }
  };

  const navigateToAddItem = () => {
    if (isEditing && shipmentId) {
      navigate(`/features/shipment-tracker/shipments/${shipmentId}/items/add`);
    } else {
      navigate('/features/shipment-tracker/shipments/new/items/add');
    }
  };

  const navigateToEditItem = (itemId: string, pendingIndex?: number) => {
    if (isEditing && shipmentId) {
      navigate(
        `/features/shipment-tracker/shipments/${shipmentId}/items/${itemId}/edit`,
      );
    } else if (pendingIndex !== undefined) {
      navigate('/features/shipment-tracker/shipments/new/items/add', {
        state: { pendingIndex },
      });
    }
  };

  const currencySymbol = CURRENCY_SYMBOL[currency] ?? currency;
  const incotermDescription = incoterm ? INCOTERM_DESCRIPTIONS[incoterm] : null;

  // Items to display: on edit page use detail.shipment.items; on create page use pendingItems
  const displayedItems = isEditing
    ? (existingShipment?.items ?? [])
    : pendingItems;

  return (
    <div className="page shipment-create-page">
      <div className="page-header">
        <ArrowBack />
        <h1>
          {isEditing ? t('shipment_edit_title') : t('shipment_create_title')}
        </h1>
      </div>

      {/* ── Name ── */}
      <div className="form-card">
        <label className="form-label">
          {t('shipment_field_name')}
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('shipment_field_name_placeholder')}
          />
        </label>
        <label className="form-label">
          {t('shipment_col_status')}
          <select
            className="input"
            value={executionStatus}
            onChange={(e) =>
              setExecutionStatus(e.target.value as ExecutionStatus)
            }
          >
            {BOARD_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`shipment_status_${status}`)}
              </option>
            ))}
          </select>
        </label>
        <ShipmentLabelsField
          labels={labelCatalog}
          selectedIds={labelIds}
          onChange={setLabelIds}
          onManageLabels={() => setShowLabelModal(true)}
        />
      </div>

      {/* ── Transport & Terms ── */}
      <div className="form-card">
        <div className="form-row">
          <label className="form-label">
            {t('shipment_field_transport_profile')} *
            <div className="shipment-transport-profile-row">
              <select
                className="input"
                value={transportProfileId}
                onChange={(e) => setTransportProfileId(e.target.value)}
                disabled={!dropdownsLoaded}
              >
                <option value="">{t('shipment_field_select_placeholder')}</option>
                <optgroup label={t('shipment_transport_group_standard')}>
                  {standardProfiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.display}
                    </option>
                  ))}
                </optgroup>
                {customProfiles.length > 0 && (
                  <optgroup label={t('shipment_transport_group_custom')}>
                    {customProfiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.display}
                      </option>
                    ))}
                  </optgroup>
                )}
                {isEditing &&
                  transportProfileId &&
                  !transportProfiles.some((p) => p.id === transportProfileId) && (
                    <option value={transportProfileId}>
                      {existingShipment?.transportProfileDisplay ?? transportProfileId}
                    </option>
                  )}
              </select>
              {canWrite && (
                <button
                  type="button"
                  className="shipment-transport-add-type-btn"
                  onClick={() => setShowTransportModal(true)}
                  aria-label={t('shipment_transport_add_btn')}
                >
                  <PlusIcon aria-hidden width={14} height={14} />
                  <span>{t('shipment_transport_add_btn_inline')}</span>
                </button>
              )}
            </div>
          </label>

          <label className="form-label">
            {t('shipment_field_incoterm')} *
            <select
              className="input"
              value={incoterm}
              onChange={(e) => setIncoterm(e.target.value)}
              disabled={!dropdownsLoaded}
            >
              <option value="">{t('shipment_field_select_placeholder')}</option>
              {incoterms.map((ic) => (
                <option key={ic.code} value={ic.code}>
                  {ic.code} - {ic.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {incotermDescription && (
          <div className="shipment-info-box shipment-info-box--blue">
            <span className="shipment-info-box__icon">✓</span>
            {incotermDescription}
          </div>
        )}

        <div className="form-row">
          <label className="form-label">
            {t('shipment_field_origin_country')} *
            <select
              className="input"
              value={originCountryCode}
              onChange={(e) => setOriginCountryCode(e.target.value)}
              disabled={!dropdownsLoaded}
            >
              <option value="">{t('shipment_field_select_placeholder')}</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} - {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="form-label">
            {t('shipment_field_origin_city')}
            <input
              className="input"
              value={originCity}
              onChange={(e) => setOriginCity(e.target.value)}
            />
          </label>
        </div>

        <div className="form-row">
          <label className="form-label">
            {t('shipment_field_destination_country')} *
            <select
              className="input"
              value={destinationCountryCode}
              onChange={(e) => setDestinationCountryCode(e.target.value)}
              disabled={!dropdownsLoaded}
            >
              <option value="">{t('shipment_field_select_placeholder')}</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} - {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="form-label">
            {t('shipment_field_destination_city')}
            <input
              className="input"
              value={destinationCity}
              onChange={(e) => setDestinationCity(e.target.value)}
            />
          </label>
        </div>

        {(originCountryCode || destinationCountryCode) && (
          <div className="shipment-info-box shipment-info-box--green">
            <span className="shipment-info-box__icon">✓</span>
            {t('shipment_import_duty_info')}
          </div>
        )}

        <div className="form-row">
          <label className="form-label">
            {t('shipment_field_eta')}
            <input
              className="input"
              type="date"
              value={eta}
              onChange={(e) => setEta(e.target.value)}
            />
          </label>
          <label className="form-label">
            {t('shipment_field_tracking_url')}
            <input
              className="input"
              type="url"
              value={trackingUrl}
              onChange={(e) => setTrackingUrl(e.target.value)}
              placeholder="https://"
            />
          </label>
        </div>

        <label className="form-label">
          {t('shipment_field_warehouse_comment')}
          <textarea
            className="input"
            rows={3}
            value={warehouseComment}
            onChange={(e) => setWarehouseComment(e.target.value)}
          />
        </label>
      </div>

      {/* ── Planned costs ── */}
      <div className="form-card">
        <h2 className="form-card__title">
          {t('shipment_section_planned_costs')}
        </h2>
        <label className="form-label">
          {t('shipment_field_currency')} *
          <select
            className="input shipment-currency-select"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
          >
            <option value={ShipmentCreateRequest.currency.EUR}>EUR - €</option>
            <option value={ShipmentCreateRequest.currency.USD}>USD - $</option>
          </select>
        </label>
        {/* Currency is required; the amounts below are not, so they sit in their own panel
            headed by the note that they can wait for the Cost breakdown tab. */}
        <div className="form-card__panel">
          <p className="form-card__panel-title">
            {t('shipment_planned_costs_hint')}
          </p>
          <hr className="form-card__panel-divider" />
          <div className="form-row">
            <label className="form-label">
              {t('cost_transport')} ({currencySymbol})
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={plannedTransport}
                onChange={(e) => setPlannedTransport(e.target.value)}
                placeholder="0.00"
              />
            </label>
            <label className="form-label">
              {t('cost_duty')} ({currencySymbol})
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={plannedDuty}
                onChange={(e) => setPlannedDuty(e.target.value)}
                placeholder="0.00"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              {t('cost_clearance')} ({currencySymbol})
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={plannedClearance}
                onChange={(e) => setPlannedClearance(e.target.value)}
                placeholder="0.00"
              />
            </label>
            <label className="form-label">
              {t('cost_energy_tax')} ({currencySymbol})
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={plannedEnergyTax}
                onChange={(e) => setPlannedEnergyTax(e.target.value)}
                placeholder="0.00"
              />
            </label>
          </div>
        </div>
      </div>

      {/* ── Items ── */}
      <div className="form-card">
        <div className="form-card__header">
          <h2 className="form-card__title">{t('shipment_section_items')}</h2>
          {canWrite && (
            <button
              className="btn btn-secondary"
              type="button"
              onClick={navigateToAddItem}
            >
              {t('shipment_add_item_button')}
            </button>
          )}
        </div>

        {displayedItems.length === 0 ? (
          <p className="shipment-items-empty">{t('shipment_items_empty')}</p>
        ) : (
          <table className="shipment-items-table">
            <thead>
              <tr>
                <th>{t('shipment_col_sku')}</th>
                <th>{t('shipment_col_qty_cartons')}</th>
                <th>{t('shipment_col_weight')}</th>
                {canViewFinancials && (
                  <th>
                    {t('shipment_col_total')} ({currencySymbol})
                  </th>
                )}
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {isEditing
                ? (existingShipment?.items ?? []).map((item) => (
                  <tr
                    key={item.id}
                    className="shipment-items-table__row"
                    onClick={() => navigateToEditItem(item.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div>{item.productName}</div>
                      <div className="text-muted">{item.sku}</div>
                    </td>
                    <td>
                      {item.units != null || item.cartons != null
                        ? `${item.units ?? '-'} / ${item.cartons ?? '-'}`
                        : '-'}
                    </td>
                    <td>
                      {item.weightPerCartonKg != null
                        ? `${item.weightPerCartonKg} kg`
                        : '-'}
                    </td>
                    {canViewFinancials && (
                      <td>
                        {item.totalItemValue != null
                          ? `${currencySymbol}${item.totalItemValue}`
                          : '-'}
                      </td>
                    )}
                    {canWrite && (
                      <td className="shipment-items-table__actions">
                        <button
                          className="btn btn-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToEditItem(item.id);
                          }}
                        >
                          {t('edit')}
                        </button>
                        <button
                          className="btn btn-link btn-link--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (shipmentId) {
                              dispatch(
                                deleteShipmentItem({
                                  shipmentId,
                                  itemId: item.id,
                                }),
                              );
                            }
                          }}
                        >
                          {t('delete')}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
                : pendingItems.map((item, idx) => (
                  <tr
                    key={idx}
                    className="shipment-items-table__row"
                    onClick={() => navigateToEditItem('', idx)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div>{item.productName}</div>
                      <div className="text-muted">{item.sku}</div>
                    </td>
                    <td>
                      {item.units != null || item.cartons != null
                        ? `${item.units ?? '-'} / ${item.cartons ?? '-'}`
                        : '-'}
                    </td>
                    <td>
                      {item.weightPerCartonKg != null
                        ? `${item.weightPerCartonKg} kg`
                        : '-'}
                    </td>
                    {canViewFinancials && (
                      <td>
                        {item.singleUnitPrice != null && item.units != null
                          ? `${currencySymbol}${(item.singleUnitPrice * item.units).toFixed(2)}`
                          : '-'}
                      </td>
                    )}
                    {canWrite && (
                      <td className="shipment-items-table__actions">
                        <button
                          className="btn btn-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToEditItem('', idx);
                          }}
                        >
                          {t('edit')}
                        </button>
                        <button
                          className="btn btn-link btn-link--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(removePendingItem(idx));
                          }}
                        >
                          {t('delete')}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="form-footer">
        {isEditing && (
          <button
            className="btn btn-danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={saving}
          >
            {t('delete')}
          </button>
        )}
        {(!isSaved || !isEditing) && (
          <button
            className="btn btn-secondary"
            onClick={handleSaveDraft}
            disabled={saving || !name.trim()}
          >
            {t('shipment_save_draft_button')}
          </button>
        )}
        <button
          className="btn btn-primary"
          onClick={handleSaveShipment}
          disabled={saving}
        >
          {t('shipment_save_button')}
        </button>
      </div>

      {showDeleteModal && existingShipment && (
        <ConfirmDeleteShipmentModal
          shipmentName={existingShipment.name}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          loading={saving}
        />
      )}

      {showLabelModal && (
        <LabelModal
          mode="create"
          onClose={() => {
            setShowLabelModal(false);
            setLabelModalError(null);
          }}
          onSave={handleCreateLabel}
          saving={labelModalSaving}
          errorMessage={labelModalError}
        />
      )}

      {showTransportModal && (
        <TransportProfileModal
          mode="create"
          onClose={() => {
            setShowTransportModal(false);
            setTransportModalError(null);
          }}
          onRequestClose={() => setShowTransportModal(false)}
          onSave={handleCreateTransport}
          saving={transportModalSaving}
          errorMessage={transportModalError}
        />
      )}
    </div>
  );
};

export default ShipmentTrackerCreatePage;
